import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import LoadingSpinner from './LoadingSpinner';
import {
  formatStatusLabel,
  getStatusBadgeClass,
  STATUS_CHART_COLORS,
} from '../utils/visaApplicationStatus';
import { formatCompanyAmount } from '../utils/currencyUtils';

const STATUS_COLORS = STATUS_CHART_COLORS;

const CHART_COLORS = ['#6941C6', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4'];

const SUMMARY_CONFIG = [
  { key: 'totalApplications', label: 'Total Applications' },
  { key: 'new', label: 'New', color: 'text-gray-600' },
  { key: 'processing', label: 'Processing', color: 'text-yellow-600' },
  { key: 'error', label: 'Error', color: 'text-red-600' },
  { key: 'finalize', label: 'Finalized', color: 'text-green-600' },
  { key: 'totalCompanies', label: 'Total Companys' },
  { key: 'activeCompanys', label: 'Active Companys' },
  { key: 'inactiveCompanies', label: 'Inactive Companys' },
  { key: 'totalManagers', label: 'Managers' },
  { key: 'balance', label: 'Account Balance', money: true },
  { key: 'totalTopUps', label: 'Total Top-ups', money: true },
  { key: 'assignedFromCountries', label: 'From Countries' },
  { key: 'assignedToCountries', label: 'To Countries' },
];

const ChartCard = ({ title, children, className = '' }) => (
  <div className={`card ${className}`}>
    <h3 className="text-base font-semibold text-gray-900 mb-4">{title}</h3>
    {children}
  </div>
);

const EmptyChart = ({ message = 'No data available' }) => (
  <div className="h-64 flex items-center justify-center text-gray-400 text-sm">{message}</div>
);

const ReportsSection = ({ title, subtitle, report, loading }) => {
  const display = report?.display || {};
  const currency = display.currency_label || display.currency || 'USD';
  const timezoneLabel = display.timezone_label || display.timezone || null;

  const formatMoney = useMemo(
    () => (value) => formatCompanyAmount(value, currency),
    [currency]
  );

  if (loading) {
    return <LoadingSpinner text="Loading report..." />;
  }

  if (!report) {
    return (
      <div className="card text-center py-12 text-gray-400">
        Failed to load report data. Please try again.
      </div>
    );
  }

  const { summary = {}, monthlyTrend = [], statusBreakdown = [] } = report;
  const pieData = statusBreakdown.filter((item) => item.value > 0);

  const summaryCards = SUMMARY_CONFIG.filter(
    (item) => summary[item.key] !== undefined && summary[item.key] !== null
  );

  const metaBits = [];
  if (display.currency_label || display.currency) {
    metaBits.push(`Currency: ${display.currency_label || display.currency}`);
  }
  if (timezoneLabel) {
    metaBits.push(`Timezone: ${timezoneLabel}`);
  }

  const renderBarChart = (data, dataKey = 'count', color = '#6941C6') => {
    if (!data?.length) return <EmptyChart />;

    return (
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 40 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 11, fill: '#6B7280' }}
            angle={-30}
            textAnchor="end"
            height={60}
            interval={0}
          />
          <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} allowDecimals={false} />
          <Tooltip
            contentStyle={{ borderRadius: '12px', border: '1px solid #E5E7EB', fontSize: '13px' }}
          />
          <Bar dataKey={dataKey} fill={color} radius={[6, 6, 0, 0]} name="Count" />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        {subtitle && <p className="text-gray-500 mt-1">{subtitle}</p>}
        {metaBits.length > 0 && (
          <p className="text-xs text-gray-400 mt-2">{metaBits.join(' · ')}</p>
        )}
      </div>

      {summaryCards.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 mb-8">
          {summaryCards.map((item) => (
            <div key={item.key} className="stats-card">
              <p className="text-gray-500 text-xs font-medium">{item.label}</p>
              <p className={`text-2xl font-bold mt-1 ${item.color || 'text-gray-900'}`}>
                {item.money ? formatMoney(summary[item.key]) : summary[item.key]}
              </p>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <ChartCard title="Monthly Application Trend">
          {monthlyTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={monthlyTrend} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#6B7280' }} />
                <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ borderRadius: '12px', border: '1px solid #E5E7EB', fontSize: '13px' }}
                />
                <Legend />
                <Bar dataKey="total" fill="#6941C6" radius={[4, 4, 0, 0]} name="Total" />
                <Bar dataKey="new" fill="#9CA3AF" radius={[4, 4, 0, 0]} name="New" />
                <Bar dataKey="processing" fill="#F59E0B" radius={[4, 4, 0, 0]} name="Processing" />
                <Bar dataKey="error" fill="#EF4444" radius={[4, 4, 0, 0]} name="Error" />
                <Bar dataKey="finalize" fill="#10B981" radius={[4, 4, 0, 0]} name="Finalized" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart message="No monthly data yet" />
          )}
        </ChartCard>

        <ChartCard title="Application Status Distribution">
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={3}
                  dataKey="value"
                  nameKey="name"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {pieData.map((entry) => (
                    <Cell
                      key={entry.key || entry.name}
                      fill={STATUS_COLORS[entry.key] || CHART_COLORS[pieData.indexOf(entry) % CHART_COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ borderRadius: '12px', border: '1px solid #E5E7EB', fontSize: '13px' }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart message="No status data yet" />
          )}
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <ChartCard title="Applications by From Country">
          {renderBarChart(report.byFromCountry, 'count', '#3B82F6')}
        </ChartCard>

        <ChartCard title="Applications by To Country">
          {renderBarChart(report.byToCountry, 'count', '#8B5CF6')}
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <ChartCard title="Applications by Visa Type">
          {renderBarChart(report.byVisaType, 'count', '#F59E0B')}
        </ChartCard>

        {report.byCompany?.length > 0 && (
          <ChartCard title="Applications by Company">
            {renderBarChart(report.byCompany, 'count', '#6941C6')}
          </ChartCard>
        )}

        {report.companyStatusBreakdown?.length > 0 && (
          <ChartCard title="Companys by Status">
            {renderBarChart(report.companyStatusBreakdown, 'count', '#10B981')}
          </ChartCard>
        )}
      </div>

      <div className="card">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Recent Applications</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">ID</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Passenger</th>
                {report.byCompany !== undefined && report.recentApplications?.[0]?.company_name && (
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Company</th>
                )}
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Route</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Visa Type</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Status</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Date</th>
              </tr>
            </thead>
            <tbody>
              {report.recentApplications?.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-8 text-gray-400">
                    No applications yet
                  </td>
                </tr>
              ) : (
                report.recentApplications?.map((app) => (
                  <tr key={app.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4 text-sm font-medium text-gray-900">#{app.id}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{app.passenger_id}</td>
                    {app.company_name && (
                      <td className="py-3 px-4 text-sm text-gray-600">{app.company_name}</td>
                    )}
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {app.from_country} → {app.to_country}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">{app.visa_type}</td>
                    <td className="py-3 px-4">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(
                          app.application_status
                        )}`}
                      >
                        {formatStatusLabel(app.application_status)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-500">
                      {app.created_at_display || new Date(app.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ReportsSection;
